<template>
  <div class="home container start-vue" :class="{ loading: loading }" :version="version">
    <Loader v-show="!initialLoadComplete" />
    <div class="" v-show="showError">
      There's been an error. We apologise for the inconvenience; please try again shortly.
    </div>
    <!-- First Row -->
    <div class="row mt-5">
      <div class="col-12 col-md-4">
        <h2 class="h3">Basic Information</h2>
        <b-form-group label="Choose your state" label-for="state">
          <b-form-select id="state" v-model="state" :options="stateOptions" :disabled="loading"></b-form-select>
        </b-form-group>

        <b-form-group label="Milk Production" label-for="milk-production">
          <b-form-input
            id="milk-production"
            v-model="localMilkProduction"
            placeholder="1,000,000"
            :disabled="loading"
            @input="formatMilkProduction"
            @change="setMilkProduction"
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Effective Date" label-for="effective-date">
          <b-form-input v-model="effectiveDate" disabled></b-form-input>
        </b-form-group>
      </div>
      <div class="col-12 col-md-8">
        <h2 class="h3">Choose Quarter</h2>
        <div class="alert alert-warning" v-show="quarter === null">Select a quarter</div>
        <div class="table--scrolling">
          <b-table v-if="quarterItems.length"
            ref="selectableTable"
            hover
            selectable
            striped
            :fields="fields"
            :items="quarters"
            :select-mode="selectMode"
            @row-selected="onRowSelected"
          >
            <template v-slot:cell(selected)="{ rowSelected }">
              <template v-if="rowSelected">
                <b-icon-circle-fill aria-hidden="true"></b-icon-circle-fill>
                <span class="sr-only">Selected</span>
              </template>
              <template v-else>
                <b-icon-circle aria-hidden="true"></b-icon-circle>
                <span class="sr-only">Not selected</span>
              </template>
            </template>

            <template v-slot:cell(quarterText)="data">
              <span :data-practice-number="data.item.practiceNumber">
                {{ data.item.year }} Q{{ data.item.quarter }} ({{ data.item.practiceNumber }})
              </span>
            </template>
            <template v-slot:cell(class)="data">
              <span>
                <b-icon
                  icon="check-circle-fill"
                  v-if="data.item.classAvailable"
                  class="text-green-600"
                  aria-hidden="true">
                </b-icon>
                <span v-else>Not available</span>
              </span>
            </template>
            <template v-slot:cell(compClassIII)="data">
              <span>
                <b-icon
                  icon="check-circle-fill"
                  v-if="data.item.compClassIIIAvailable"
                  class="text-green-600"
                  aria-hidden="true">
                </b-icon>
                <span v-else>Not available</span>
              </span>
            </template>
            <template v-slot:cell(compClassIV)="data">
              <span>
                <b-icon
                  icon="check-circle-fill"
                  v-if="data.item.compClassIVAvailable"
                  class="text-green-600"
                  aria-hidden="true">
                </b-icon>
                <span v-else>Not available</span>
              </span>
            </template>
          </b-table>
        </div>
      </div>
    </div>
    <!-- Second Row -->
    <div class="mt-8">
      <b-tabs v-model="tabIndex" @input="updateQuoteTypeEvent" justified>
        <b-tab title="Class Pricing">
          <ClassView />
        </b-tab>
        <b-tab title="Component Pricing">
          <ComponentView />
        </b-tab>
        <b-tab title="Blended Pricing" active>
          <Blended />
        </b-tab>
      </b-tabs>
    </div>

    <div class="box border mb-8" v-if="quoteState && queryLoaded">
      <h3 class="h3">Dairy RP Quote for {{states[quoteState].name}}</h3>
      <div class="alert alert-warning mt-4" v-show="quarter === null">Please select a quarter above!</div>
      <ResultsTable v-if="queryLoaded" />
      <div class="alert alert-info" v-if="quoteState && queryLoaded">
        Quote generated for {{quoteState}} for practice {{quarter}} with a sales
        effective date of {{this.$store.state.effectiveDate}}. <strong>The above is only an
        estimate.</strong> Additional rate discounts and surcharges may apply.
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapActions, mapMutations, mapState } from 'vuex';
import {
  getPracticePeriods,
  getOffers,
  getPriceRows,
} from '../queries/getPracticePeriods.gql';
import Blended from './Blended.vue';
import ClassView from './ClassView.vue';
import ComponentView from './ComponentView.vue';
import Loader from '../components/Loader.vue';
import ResultsTable from '../components/ResultsTable.vue';
import { normalinv, parseISOLocal } from '../scripts/utils';
import States from '../constants/states';

const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };

export default {
  name: 'Start',
  components: {
    Blended,
    ClassView,
    ComponentView,
    Loader,
    ResultsTable,
  },

  data() {
    return {
      status: 'loading',
      tabIndex: 2,
      date: new Date().toLocaleString('en-US', dateOptions),
      cachedData: {},
      tabs: ['class', 'component', 'blended'],
      generalQuoteInfoMessage:
        'These values represent common or average values for your region so we can give you a rough estimate of what your premium might be. You can edit the values to get a more accurate quote, or call us and we can guide you through and get you taken care of!',

      // Data for the Quarter Selection table
      selectMode: 'single',
      fields: [
        { key: 'selected', label: '' },
        { key: 'quarterText', label: 'Quarter', thStyle: 'width: 33%' },
        { key: 'class', label: 'Class', class: 'text-center' },
        { key: 'compClassIII', label: 'Component Class III', class: 'text-center' },
        { key: 'compClassIV', label: 'Component Class IV', class: 'text-center' },
      ],

      items: [],
      initialLoadComplete: false,
      initialQuarterSelection: false,
      quarters: [],
      error: null,
      localMilkProduction: '1,000,000',
    };
  },

  apollo: {
    practicePeriods: {
      query: getPracticePeriods,
      result({ data }) {
        if (data.practicePeriods && data.practicePeriods.length > 0) {
          const sorted = data.practicePeriods.sort((a, b) => a.practiceNumber - b.practiceNumber);
          this.quarters = this.createQuarterItems(sorted);
          sorted.forEach(q => {
            if (!this.cachedData[q.practiceNumber]) {
              this.cachedData[q.practiceNumber] = {};
            }
          });
          this.initialLoadComplete = true;
        } else {
          this.status = 'error';
        }
      },
    },

    offers: {
      query: getOffers,
      variables() {
        return {
          practiceCode: this.quarter,
          stateCode: this.states[this.state].code,
        };
      },
      skip() {
        return this.skipOffersQuery;
      },
      result({ data }) {
        if (data.offers && data.offers.length) {
          const classOffer = data.offers.find(offer => offer.typeCode == 831);
          this.insuranceOfferId = classOffer.admInsuranceOfferId || data.offers[0].admInsuranceOfferId;
          this.initialLoadComplete = true;
        }
      },
    },

    priceRows: {
      query: getPriceRows,

      variables() {
        return {
          practiceCode: this.quarter,
          stateCode: this.states[this.state].code,
        };
      },

      skip() {
        return this.skipOffersQuery;
      },

      result({ data }) {
        if (data.priceRows && data.priceRows.length) {
          const effectiveDate = parseISOLocal(`${data.priceRows[0].fileDate}T00:00:00`);
          const priceRow = data.priceRows[0].combinedData;
          const { simPrices, expectedPrice, yieldAdjustmentFactor } = priceRow;
          this.updateQuarterData({
            effectiveDate,
            simPrices,
            expectedPrice,
            yieldAdjustmentFactor,
            queryLoaded: this.queryLoaded,
          });

          this.cachedData[this.quarter] = priceRow;
          this.initialLoadComplete = true;
          this.initialQuarterSelection = false;
        } else {
          console.log('There was an issue retrieving Price Rows');
        }
      },
    },
  },

  computed: {
    ...mapState([
      'availability',
      'expectedRevenueAmount',
      'expectedRevenueGuarantee',
      'simulatedLoss',
      'simulatedRevenueAmount',
      'simYieldAdjustmentFactor',
      'states',
      'queryLoaded',
      'loading',
      'effectiveDate',
      'milkProduction',
      'version',
    ]),

    showError() {
      return !!(this.status === 'error');
    },

    quarterItems() {
      if (!this.quarters.length) return [];
      return this.quarters;
    },

    skipOffersQuery() {
      if (this.quarter === null) return true;
      if (!this.state) return true;
      return false;
    },

    stateOptions() {
      return [
        { value: null, text: 'Please select your state' },
        ...Object.keys(this.states).map((key) => ({ value: key, text: this.states[key].name })),
      ];
    },

    state: {
      get() {
        return this.$store.state.state;
      },
      set(value) {
        this.$store.commit('updateState', value);
      },
    },

    quarter: {
      get() {
        return this.$store.state.quarter;
      },
      set(value) {
        this.$store.commit('updateQuarter', value);
      },
    },

    insuranceOfferId: {
      get() {
        return this.$store.state.insuranceOfferId;
      },
      set(value) {
        this.$store.commit('updateInsuranceOfferId', value);
      },
    },

    quoteState() {
      return this.$store.state.state ? this.$store.state.state : false;
    },
  },

  methods: {
    ...mapMutations([
      'updateMilkProduction',
      'updateQuoteType',
      'updateSimPrices',
      'updateExpPrice',
      'updateQueryLoaded',
      'updateStates',
      'updateQuarter',
      'updateAvailability',
      'updateSimulatedYieldAdjustmentFactor',
      'updateInsuranceBlend',
      'updateEffectiveDate',
      'updateLoading',
      'updateCalculating',
      'updateComponentPercent',
    ]),

    ...mapActions(['updateQuarterData']),

    onRowSelected(items) {
      if (this.quarter === null) this.initialQuarterSelection = true;
      if (items.length) {
        const selection = items[0];
        let newValue = parseInt(selection.value);
        if (newValue === this.quarter) return;

        this.$store.commit('updateLoading', true);
        this.updateAvailability(selection.availability);

        const classAvailable = selection.availability.class;
        const {
          componentClassIII,
          componentClassIV,
        } = selection.availability;

        // If somehow the user manages to select a quarter in which no DRP is available at all
        if (!componentClassIII && !componentClassIV && !classAvailable) {
          this.quarter = null;
          return;
        }

        if (componentClassIII && !componentClassIV) this.updateComponentPercent('0');
        if (!componentClassIII && componentClassIV) this.updateComponentPercent('100');
        if (!componentClassIII && !componentClassIV) this.updateInsuranceBlend(0);
        if (!classAvailable) { this.updateInsuranceBlend(100); }

        this.quarter = newValue;

        if (items.length > 1) {
          this.error = 'More than one quarter was selected';
          return console.log(error);
        }
      }
    },

    setMilkProduction(value) {
      this.updateLoading(true);
      const numberValue = value.replace(/\D/g,'');
      this.localMilkProduction = numberValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      this.updateMilkProduction(parseInt(numberValue));
    },

    formatMilkProduction(value) {
      const numberValue = value.replace(/\D/g,'');
      this.localMilkProduction = numberValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    createQuarterItems() {
      if (!this.practicePeriods) return [];
      return this.practicePeriods.map((quarter) => {
        const quarterItem = quarter;
        quarterItem.selected = false;
        quarterItem.rowSelected = false;
        // if (quarterItem.quarter === 2) {
        //   quarterItem.rowSelected = true;
        //   quarterItem.selected = true;
        // }
        return this.createQuarterItem(quarterItem);
      });
    },

    createQuarterItem({
      year,
      quarter,
      availability,
      practiceNumber,
      rowSelected,
    }) {
      const parsedAvailability = availability;
      const classAvailable = parsedAvailability.class;
      const compClassIIIAvailable = parsedAvailability.componentClassIII;
      const compClassIVAvailable = parsedAvailability.componentClassIV;
      return {
        quarterId: `${year}-Q${quarter}`,
        year,
        quarter,
        availability,
        value: `${practiceNumber}`,
        practiceNumber: `${practiceNumber}`,
        text: `${year} Q${quarter}`,
        classAvailable,
        compClassIIIAvailable,
        compClassIVAvailable,
        rowSelected,
      };
    },

    updateQuoteTypeEvent(tabIndex) {
      this.updateQuoteType(tabIndex);
      const blend = ((index) => {
          switch(index) {
            case 0:
              return 0;
            case 1:
              return 100;
            default:
              return 50;
          }
      })(tabIndex);

      this.updateInsuranceBlend(blend);
    },
  },

  mounted() {
    this.updateLoading(false);
    // this.unsubscribe = this.$store.subscribe((mutation, state) => {
    //   const mutations = [
    //     'updateComponentPercent',
    //     'updateClassPercent',
    //     'updateProtectionFactorClass',
    //     'updateProteinTest',
    //     'updateButterfatTest',
    //     'updateProtectionFactorComponent',
    //     'updateComponentPercent',
    //     'updateProtectionFactorComponent',
    //     'updateCoverageLevel',
    //   ];

    //   if (mutations.indexOf(mutation.type) > -1) {
    //     this.updateLoading(true);
    //   };
    // }, { prepend: true });

    this.unwatch = this.$store.watch(
      (state, getters) => {
        return {
          premium: getters.producerPremiumAmount,
          rev: getters.revGuarantee,
        };
      },
      ({premium, rev}, old) => {
        if (premium !== old.premium || rev !== old.rev) {
          this.$store.commit('updateLoading', false);
        }
      },
    );
  },
  beforeDestroy() {
    // this.unsubscribe();
    this.unwatch();
  },
};
</script>

<style>
.table--scrolling {
  width: 100%;
  overflow-x: scroll;
}
</style>
